import { useContext, useEffect, useCallback, useState } from 'react';

// Authentication
import { AuthContext } from 'services/auth';

// Material UI components / helpers
import {
  Grid,
  makeStyles,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@material-ui/core';

// Luxon date helpers
import { DateTime } from 'luxon';

// AsyncStatus
import { AsyncStatus } from 'components';

// To handle error messages
import { getErrorMessage } from 'helpers';

// Local components
import { FigureCard, BookingsChart, BookingsListCard } from './components';
import { getParamByParam } from 'iso-country-currency';
import { Refresh } from '@mui/icons-material';

// Add TabContainer import
import { TabContainer } from 'components';

// Add Firebase import
import firebase from 'firebase/app';

// Component styles
import styles from './styles';
const useStyles = makeStyles(styles);

const paymentReducer = (last, current) => {
  // Handle both regular bookings (payment.rate) and group session bookings (payment.amount)
  const paymentAmount = current.payment
    ? current.payment.rate || current.payment.amount || 0
    : 0;
  return last + paymentAmount;
};

const getCurrencySymbol = (currencyCode) => {
  if (!currencyCode) return '';
  try {
    return getParamByParam('currency', currencyCode, 'symbol');
  } catch (e) {
    console.warn(`Could not find currency symbol for ${currencyCode}.`);
    return currencyCode; // Fallback to currency code
  }
};

function Income(props) {
  const auth = useContext(AuthContext);
  const classes = useStyles();

  // Setup component state
  const [bookings, setBookings] = useState(null);
  const [groupSessionBookings, setGroupSessionBookings] = useState(null);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(false);
  const [tutor, setTutor] = useState(null);
  const [selectedValue, setSelectedValue] = useState(1);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [currentIncomeTab, setCurrentIncomeTab] = useState('one-on-one');
  // Remove groupSessions and groupSessionsLoaded state - no longer needed
  // Define the state to store the current date for periodic updates
  const [currentDate, setCurrentDate] = useState(
    DateTime.local().setZone('Australia/Darwin')
  );

  // Callback function for retrieving one-on-one bookings
  const getBookings = useCallback(
    async (startAt, endAt) => {
      try {
        setBookings(null);
        console.log('startAtA', startAt);
        console.log('endAtA', endAt);

        const updateBookings = (newBookings) => {
          console.log('newBookings', newBookings);
          setBookings(newBookings);
        };

        await auth.api._bookings.getCompletedBookings(
          props.uid || auth.user.uid,
          startAt,
          endAt,
          true,
          updateBookings
        );
      } catch (error) {
        setError(getErrorMessage(error));
      }
    },
    [auth.api._bookings, auth.user.uid, props.uid]
  );

  // Simplified group session bookings function
  const getGroupSessionBookings = useCallback(
    async (startAt, endAt) => {
      try {
        setGroupSessionBookings(null);

        // Fetch group sessions directly within date range
        const groupSessionsSnapshot = await firebase
          .firestore()
          .collection('groupsessions')
          .where('tutor', '==', props?.uid ?? auth?.user?.uid)
          .get();

        if (groupSessionsSnapshot.empty) {
          console.log('No group sessions found');
          setGroupSessionBookings([]);
          return;
        }

        // Extract bookings from group sessions within date range
        const groupBookings = [];
        groupSessionsSnapshot.forEach((doc) => {
          const session = { id: doc.id, ...doc.data() };

          if (Array.isArray(session.bookings)) {
            session.bookings.forEach((booking) => {
              if (
                booking.when >= startAt &&
                booking.when <= endAt &&
                (booking.status === 'completed' ||
                  booking.status === 'student-absent')
              ) {
                // Normalize the booking structure
                const normalizedBooking = {
                  ...booking,
                  payment: {
                    ...booking.payment,
                    rate: booking.payment?.amount || 0,
                    paid: booking.payment?.paid || false
                  },
                  profile: {
                    student: {
                      name: `${booking.students[0]?.name} with ${
                        booking.students?.length - 1 || 0
                      } other Students`,
                      image: booking.students[0]?.image || '',
                      tutor: booking[0]?.tutor || session.profile?.tutor || {}
                    }
                  },
                  sessionId: session.id,
                  sessionTitle: session?.title || 'Group Session',
                  sessionMaxStudents: session?.maxStudentCount || 0,
                  sessionMinStudents: session?.minStudentCount || 0,
                  sessionLessonCount: session?.lessonCount || 0,
                  id: booking.bookingId,
                  uid: booking.bookingId,
                  tutorInfo: booking.tutor || session.profile?.tutor || {},
                  student: `${booking.students[0]?.name} with ${
                    booking.students?.length - 1 || 0
                  } other Students`,
                  studentsInfo: booking.students || [],
                  studentCount: booking.students?.length || 0
                };

                groupBookings.push(normalizedBooking);
              }
            });
          }
        });

        console.log('Filtered group session bookings:', groupBookings);
        setGroupSessionBookings(groupBookings);
      } catch (error) {
        setError(getErrorMessage(error));
        console.error('Error fetching group session bookings:', error);
        setGroupSessionBookings([]);
      }
    },
    [auth.user.uid, props.uid]
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentDate(DateTime.local().setZone('Australia/Darwin'));
    }, 1000 * 60 * 60); // Check every hour, adjust as needed

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    async function getTutorProfile() {
      try {
        // Get the tutor profile
        const profile = await auth.api.profile.getTutorProfile(
          props.uid ?? auth.user.uid,
          false
        );
        if (profile === null) {
          setError('The teacher profile was not found');
        } else {
          setTutor(profile);
          // Load initial data for both tabs
          handleRefreshCurrentWeek();
        }
      } catch (error) {
        setError(error);
      }
    }

    // Calculate initial date range
    const startDate = currentDate
      .minus({ weeks: 1 })
      .startOf('week')
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const endDate = startDate.plus({
      days: 6,
      hours: 23,
      minutes: 59,
      seconds: 59,
      milliseconds: 999
    });

    // Initialize group session bookings with date range
    getGroupSessionBookings(startDate.toMillis(), endDate.toMillis());
    getTutorProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retry]);

  const retryAPICall = useCallback(() => {
    setError(null);
    setRetry(!retry);
  }, [retry]);

  // Display the error if an error occured
  if (error) {
    return <AsyncStatus retry gutterTop error={error} onRetry={retryAPICall} />;
  }

  // Update calculations to use appropriate data based on current tab
  const currentBookings =
    currentIncomeTab === 'group-session' ? groupSessionBookings : bookings;

  const paidBookings =
    currentBookings &&
    currentBookings.filter(
      (booking) => booking.payment && booking.payment.paid === true
    );
  const unpaidBookings =
    currentBookings &&
    currentBookings.filter(
      (booking) => booking.payment && booking.payment.paid === false
    );
  const amountPaid = paidBookings && paidBookings.reduce(paymentReducer, 0);
  const amountUnpaid =
    unpaidBookings && unpaidBookings.reduce(paymentReducer, 0);

  const generateDateRanges = () => {
    const dateRanges = [];
    const currentDayOfWeek = currentDate.weekday; // 1 (Monday) to 7 (Sunday)

    for (let i = 0; i < 6; i++) {
      // Calculate the days to subtract to reach the Monday of each full week
      const daysToSubtract = currentDayOfWeek + i * 7;

      const startDate = currentDate
        .minus({ days: daysToSubtract })
        .startOf('week')
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      const endDate = startDate.plus({ days: 6 }).endOf('day'); // Set to the end of the day

      const formattedStartDate = startDate.toLocaleString({
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true // Use 12-hour clock
      });

      const formattedEndDate = endDate.toLocaleString({
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true // Use 12-hour clock
      });

      const dateRangeLabel = `Payment Period [${formattedStartDate} to ${formattedEndDate} ACST]`;

      dateRanges.push({
        startDate: startDate.toMillis(),
        endDate: endDate.toMillis(),
        label: dateRangeLabel,
        value: i + 1
      });
    }

    return dateRanges;
  };

  const handleMenuItemClick = (startDate, endDate, value, label) => {
    const periodString = label;
    setSelectedValue(value);
    setSelectedPeriod(periodString);

    if (currentIncomeTab === 'group-session') {
      getGroupSessionBookings(startDate, endDate);
    } else {
      getBookings(startDate, endDate);
    }
  };

  // Optimized handleRefreshCurrentWeek function
  const handleRefreshCurrentWeek = () => {
    const startDate = currentDate
      .minus({ weeks: 1 })
      .startOf('week')
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

    const endDate = startDate.plus({
      days: 6,
      hours: 23,
      minutes: 59,
      seconds: 59,
      milliseconds: 999
    });

    const dateRangesLocal = generateDateRanges();
    const previousWeekIndex = dateRangesLocal.findIndex(
      (range) =>
        range.startDate === startDate.toMillis() &&
        range.endDate === endDate.toMillis()
    );

    const selectedValue =
      previousWeekIndex !== -1
        ? dateRangesLocal[previousWeekIndex].value
        : dateRangesLocal[0].value;

    const selectedLabel =
      previousWeekIndex !== -1
        ? dateRangesLocal[previousWeekIndex].label
        : dateRangesLocal[0].label;

    setSelectedValue(selectedValue);
    setSelectedPeriod(selectedLabel);

    // Load data for current tab
    if (currentIncomeTab === 'group-session') {
      getGroupSessionBookings(startDate.toMillis(), endDate.toMillis());
    } else {
      getBookings(startDate.toMillis(), endDate.toMillis());
    }
  };

  // Simplified tab change handler
  const handleIncomeTabChange = (tabLabel) => {
    const newTab = tabLabel.toLowerCase().replace(' ', '-').toLowerCase();
    setCurrentIncomeTab(newTab);

    // Reset bookings when switching tabs
    setBookings(null);
    setGroupSessionBookings(null);

    // Calculate date range for current period
    const startDate = currentDate
      .minus({ weeks: 1 })
      .startOf('week')
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const endDate = startDate.plus({
      days: 6,
      hours: 23,
      minutes: 59,
      seconds: 59,
      milliseconds: 999
    });

    if (newTab === 'group-session') {
      getGroupSessionBookings(startDate.toMillis(), endDate.toMillis());
    } else {
      getBookings(startDate.toMillis(), endDate.toMillis());
    }
  };

  const dateRanges = generateDateRanges();

  // Create the one-on-one income content component
  const OneOnOneIncomeContent = () => {
    const oneOnOneBookings = bookings;
    const oneOnOnePaidBookings =
      oneOnOneBookings &&
      oneOnOneBookings.filter(
        (booking) => booking.payment && booking.payment.paid === true
      );
    const oneOnOneUnpaidBookings =
      oneOnOneBookings &&
      oneOnOneBookings.filter(
        (booking) => booking.payment && booking.payment.paid === false
      );
    const oneOnOneAmountPaid =
      oneOnOnePaidBookings && oneOnOnePaidBookings.reduce(paymentReducer, 0);
    const oneOnOneAmountUnpaid =
      oneOnOneUnpaidBookings &&
      oneOnOneUnpaidBookings.reduce(paymentReducer, 0);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Paper className={classes.dateControls}>
            <div className={classes.container}>
              <FormControl className={classes.formControl} variant="outlined">
                <InputLabel id="one-on-one-select-label">
                  Previous One-on-One Payment Records
                </InputLabel>
                <Select
                  className={classes.select}
                  labelId="one-on-one-select-label"
                  id="one-on-one-select"
                  label="Previous One-on-One Payment Records"
                  value={selectedValue}
                  MenuProps={{
                    classes: {
                      paper: classes.paper
                    }
                  }}
                  onChange={(event) => setSelectedValue(event.target.value)}>
                  {dateRanges.map((range) => {
                    return (
                      <MenuItem
                        key={range.value}
                        value={range.value}
                        onClick={() => {
                          setSelectedValue(range.value);
                          setSelectedPeriod(range.label);
                          getBookings(range.startDate, range.endDate);
                        }}>
                        {range.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Button
                className={classes.button}
                size="large"
                variant="contained"
                color="primary"
                onClick={() => {
                  const startDate = currentDate
                    .minus({ weeks: 1 })
                    .startOf('week')
                    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
                  const endDate = startDate.plus({
                    days: 6,
                    hours: 23,
                    minutes: 59,
                    seconds: 59,
                    milliseconds: 999
                  });
                  getBookings(startDate.toMillis(), endDate.toMillis());
                }}
                startIcon={<Refresh />}>
                Refresh Current Payment Period
              </Button>
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
          <FigureCard
            name="Paid Lessons"
            value={
              oneOnOneBookings && oneOnOnePaidBookings
                ? oneOnOnePaidBookings.length.toString()
                : '0'
            }
          />
        </Grid>
        <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
          <FigureCard
            name="Paid Income"
            value={
              oneOnOneBookings && oneOnOneAmountPaid !== null
                ? `${getCurrencySymbol(
                    tutor?.profile?.currency
                  )} ${oneOnOneAmountPaid.toFixed(2).toString()}`
                : '0'
            }
          />
        </Grid>
        <Grid item xs={12} md={3} lg={3} xl={3}>
          <FigureCard
            name="Unpaid Lessons"
            value={
              oneOnOneBookings && oneOnOneUnpaidBookings
                ? oneOnOneUnpaidBookings.length.toString()
                : '0'
            }
          />
        </Grid>
        <Grid item xs={12} md={3} lg={3} xl={3}>
          <FigureCard
            name="Unpaid Income"
            value={
              oneOnOneBookings && oneOnOneAmountUnpaid !== null
                ? `${getCurrencySymbol(
                    tutor?.profile?.currency
                  )} ${oneOnOneAmountUnpaid.toFixed(2).toString()}`
                : '0'
            }
          />
        </Grid>
        <Grid item xs={12} sm={12} md={7} lg={8} xl={9}>
          <BookingsChart
            error={error}
            tutor={tutor}
            bookings={oneOnOneBookings}
            selectedPeriod={selectedPeriod}
            isGroupSession={false}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={5} lg={4} xl={3}>
          <BookingsListCard
            bookings={oneOnOneBookings}
            tutor={tutor}
            isGroupSession={false}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}></Grid>
      </Grid>
    );
  };

  // Create the group session income content component
  const GroupSessionIncomeContent = () => {
    const groupSessionPaidBookings =
      groupSessionBookings &&
      groupSessionBookings.filter(
        (booking) => booking.payment && booking.payment.paid === true
      );
    const groupSessionUnpaidBookings =
      groupSessionBookings &&
      groupSessionBookings.filter(
        (booking) => booking.payment && booking.payment.paid === false
      );
    const groupSessionAmountPaid =
      groupSessionPaidBookings &&
      groupSessionPaidBookings.reduce(paymentReducer, 0);
    const groupSessionAmountUnpaid =
      groupSessionUnpaidBookings &&
      groupSessionUnpaidBookings.reduce(paymentReducer, 0);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Paper className={classes.dateControls}>
            <div className={classes.container}>
              <FormControl className={classes.formControl} variant="outlined">
                <InputLabel id="group-session-select-label">
                  Previous Group Session Payment Records
                </InputLabel>
                <Select
                  className={classes.select}
                  labelId="group-session-select-label"
                  id="group-session-select"
                  label="Previous Group Session Payment Records"
                  value={selectedValue}
                  MenuProps={{
                    classes: {
                      paper: classes.paper
                    }
                  }}
                  onChange={(event) => setSelectedValue(event.target.value)}>
                  {dateRanges.map((range) => {
                    return (
                      <MenuItem
                        key={range.value}
                        value={range.value}
                        onClick={() => {
                          setSelectedValue(range.value);
                          setSelectedPeriod(range.label);
                          getGroupSessionBookings(
                            range.startDate,
                            range.endDate
                          );
                        }}>
                        {range.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Button
                className={classes.button}
                size="large"
                variant="contained"
                color="primary"
                onClick={() => {
                  const startDate = currentDate
                    .minus({ weeks: 1 })
                    .startOf('week')
                    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
                  const endDate = startDate.plus({
                    days: 6,
                    hours: 23,
                    minutes: 59,
                    seconds: 59,
                    milliseconds: 999
                  });
                  getGroupSessionBookings(
                    startDate.toMillis(),
                    endDate.toMillis()
                  );
                }}
                startIcon={<Refresh />}>
                Refresh Current Payment Period
              </Button>
            </div>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
          <FigureCard
            name="Paid Group Sessions"
            value={
              groupSessionBookings && groupSessionPaidBookings
                ? groupSessionPaidBookings.length.toString()
                : '0'
            }
          />
        </Grid>
        <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
          <FigureCard
            name="Paid Income"
            value={
              groupSessionBookings && groupSessionAmountPaid !== null
                ? `${getCurrencySymbol(
                    tutor?.profile?.currency
                  )} ${groupSessionAmountPaid.toFixed(2).toString()}`
                : '0'
            }
          />
        </Grid>
        <Grid item xs={12} md={3} lg={3} xl={3}>
          <FigureCard
            name="Unpaid Group Sessions"
            value={
              groupSessionBookings && groupSessionUnpaidBookings
                ? groupSessionUnpaidBookings.length.toString()
                : '0'
            }
          />
        </Grid>
        <Grid item xs={12} md={3} lg={3} xl={3}>
          <FigureCard
            name="Unpaid Income"
            value={
              groupSessionBookings && groupSessionAmountUnpaid !== null
                ? `${getCurrencySymbol(
                    tutor?.profile?.currency
                  )} ${groupSessionAmountUnpaid.toFixed(2).toString()}`
                : '0'
            }
          />
        </Grid>
        <Grid item xs={12} sm={12} md={7} lg={8} xl={9}>
          <BookingsChart
            error={error}
            tutor={tutor}
            bookings={groupSessionBookings}
            selectedPeriod={selectedPeriod}
            isGroupSession={true}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={5} lg={4} xl={3}>
          <BookingsListCard
            bookings={groupSessionBookings}
            tutor={tutor}
            isGroupSession={true}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}></Grid>
      </Grid>
    );
  };

  return (
    <div className={props.padding ? classes.root : ''}>
      <TabContainer
        onTabChange={handleIncomeTabChange}
        tabs={[
          {
            label: 'One-on-One',
            component: <OneOnOneIncomeContent />
          },
          {
            label: 'Group Session',
            component: <GroupSessionIncomeContent />
          }
        ]}
      />
    </div>
  );
}

export default Income;
